import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { PowerState, Screen } from '@noorview/core';
import { ASPECTS } from '@noorview/core';
import { attempt, backend, openVirtualScreen, screenStatus, seenAgo, STATUS_LABEL, STATUS_PILL, useApp, useScreenBroadcasts } from '../data/store';
import { Icon } from '../ui/icons';
import { IconButton, Segmented, Switch, confirm } from '../ui/controls';
import { StageView } from '../ui/StageView';
import { useLocked } from '../app/lock';
import { openSheet, useNav } from '../app/ui';
import { designOf, liveDesign } from '../app/designs';

export function ScreensPage() {
  const ws = useApp((s) => s.ws)!;
  const now = useApp((s) => s.now);
  // Opened from a screen on the dashboard: scroll to that screen and highlight it for a moment.
  const [params, setParams] = useSearchParams();
  const want = params.get('screen');
  const [flash, setFlash] = useState<string | null>(null);
  useEffect(() => {
    if (!want) return;
    const el = document.getElementById(`screen-${want}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlash(want);
    setParams({}, { replace: true });
    const t = setTimeout(() => setFlash(null), 1800);
    return () => clearTimeout(t);
  }, [want]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="page">
      <header className="page-head">
        <div><h1>Screens</h1><p>{ws.screens.length} screen{ws.screens.length === 1 ? '' : 's'} at {ws.masjid.name}</p></div>
        <span className="spacer" />
        {backend.kind === 'sandbox' && <button type="button" className="btn secondary" onClick={() => openVirtualScreen()}><Icon name="tv" size={18} />Open virtual screen</button>}
        <button type="button" className="btn primary" onClick={() => openSheet({ name: 'addScreen' })}><Icon name="plus" size={18} />Add Screen</button>
      </header>
      {!ws.screens.length && <div className="card glass empty"><b>No screens yet</b><span>Add a screen, then pair the display on the wall.</span></div>}
      <div className="screen-cards">{ws.screens.map((s) => <ScreenCard key={s.id} s={s} now={now} flash={flash === s.id} />)}</div>
    </div>
  );
}

function ScreenCard({ s, now, flash }: { s: Screen; now: number; flash?: boolean }) {
  const ws = useApp((x) => x.ws)!;
  const nav = useNav();
  const [name, setName] = useState<string | null>(null);
  const systemLocked = useLocked();
  // Lock means lockdown: a locked screen (or all of NoorView locked) can't be changed at all until it's unlocked.
  const locked = systemLocked || s.locked;
  const st = screenStatus(s, now);
  const rec = designOf(ws, s.id);
  const d = liveDesign(ws, s.id, now);
  const bcs = useScreenBroadcasts(s.id);
  const upToDate = rec && s.showingVersion != null && s.showingVersion >= rec.publishedVersion;
  const saveName = async () => { if (name && name.trim() && name.trim() !== s.name) await attempt(() => backend.updateScreen(s.id, { name: name.trim() })); setName(null); };
  return (
    <article id={`screen-${s.id}`} className={`card glass screen-card${flash ? ' flash' : ''}`}>
      <button type="button" className="sc-thumb" onClick={() => openSheet({ name: 'fullscreen', screenId: s.id })} aria-label={`Open ${s.name} full screen`}>
        {d ? <StageView design={d} masjid={ws.masjid} timetable={ws.timetable} power={s.power} salahBlank={s.salahBlank !== false} broadcasts={bcs} className="stage-fill" label={`${s.name} preview`} /> : <span className="hint">Nothing published yet</span>}
      </button>
      <div className="sc-head">
        {name === null ? <h2 onDoubleClick={() => { if (!locked) setName(s.name); }} title={locked ? undefined : 'Double-click to rename'}>{s.name}</h2>
          : <input className="input" autoFocus value={name} maxLength={40} onChange={(e) => setName(e.target.value)} onBlur={saveName} onKeyDown={(e) => { if (e.key === 'Enter') void saveName(); if (e.key === 'Escape') setName(null); }} aria-label="Screen name" />}
        <span className={`pill ${STATUS_PILL[st]}`}>{STATUS_LABEL[st]}</span>
        {s.locked && <span className="pill off" title="Locked: nothing on this screen can be changed until it’s unlocked">Locked</span>}
        <span className="spacer" />
        <IconButton icon="pencil" label="Rename" size="sm" disabled={locked} onClick={() => setName(s.name)} />
      </div>
      <dl className="sc-meta">
        <div><dt>Display</dt><dd>{s.deviceId ? `${s.deviceName ? `${s.deviceName} · ` : ''}${seenAgo(s, now)}` : 'Not paired'}</dd></div>
        <div><dt>Format</dt><dd>{rec ? `${ASPECTS[rec.draft.aspect].label} · ${ASPECTS[rec.draft.aspect].width} × ${ASPECTS[rec.draft.aspect].height}` : '—'}</dd></div>
        <div><dt>Live version</dt><dd>{rec?.publishedVersion ? `v${rec.publishedVersion}${s.deviceId ? (upToDate ? ' · showing' : s.showingVersion != null ? ` · screen on v${s.showingVersion}` : '') : ''}` : 'Not published'}</dd></div>
        <div><dt>Schedules</dt><dd>{ws.schedules.filter((x) => x.screenId === s.id).length || 'None'}</dd></div>
      </dl>
      <div className="row">
        <span className="label">Power</span><span className="spacer" />
        <Segmented size="sm" className={`power-seg p-${s.power}`} label={`${s.name} power`} value={s.power} onChange={(p: PowerState) => attempt(() => backend.updateScreen(s.id, { power: p }))}
          options={[{ value: 'on', label: 'On', disabled: locked }, { value: 'idle', label: 'Idle', disabled: locked }, { value: 'off', label: 'Off', disabled: locked }]} />
      </div>
      <div className="row"><span className="label">Lock</span><small className="hint" style={{ flex: 1 }}>{s.locked ? 'Nothing on this screen can be changed until you unlock it' : 'Stops any changes to this screen until it’s unlocked'}</small><Switch label={`Lock ${s.name}`} checked={s.locked} onChange={(v) => attempt(() => backend.updateScreen(s.id, { locked: v }))} /></div>
      <div className="sc-actions">
        <button type="button" className="btn sm primary" onClick={() => nav(`/studio/${s.id}?start=live`)}><Icon name="studio" size={16} />Edit in Studio</button>
        <button type="button" className="btn sm plain" disabled={locked} onClick={() => openSheet({ name: 'schedule', screenId: s.id })}><Icon name="schedule" size={16} />Schedule</button>
        <button type="button" className="btn sm plain" disabled={locked} onClick={() => openSheet({ name: 'pair', screenId: s.id })}><Icon name="tv" size={16} />{s.deviceId ? 'Replace display' : 'Pair display'}</button>
        {s.deviceId && <button type="button" className="btn sm plain" disabled={locked} onClick={async () => { if (await confirm(`Unpair ${s.name}?`, { body: 'The display will go back to showing a pairing code.', ok: 'Unpair' })) await attempt(() => backend.unpairScreen(s.id), 'Display unpaired'); }}><Icon name="link" size={16} />Unpair</button>}
        <button type="button" className="btn sm plain danger-text" disabled={locked} onClick={async () => { if (await confirm(`Delete ${s.name}?`, { body: 'Its design and schedules are removed. This can’t be undone.', ok: 'Delete', danger: true })) await attempt(() => backend.deleteScreen(s.id), 'Screen deleted'); }}><Icon name="trash" size={16} />Delete</button>
      </div>
    </article>
  );
}
